import React, {FC, useMemo, useState} from "react";
import {DeleteOutlined, EditOutlined, MailOutlined, PlusOutlined, StopOutlined, UndoOutlined} from "@ant-design/icons";
import {Button, Form, Input, Modal, Select, Space, Switch, Table, Typography, App} from "antd";
import type {TableColumnsType} from "antd";
import {useTranslation} from "react-i18next";
import {
    EmailDeliveryStatus,
    UserRole,
    useCreateOrganizationMemberMutation,
    useOrganizationMembersQuery,
    useRemovePendingOrganizationMemberInviteMutation,
    useResendOrganizationMemberOnboardingMutation,
    useUpdateOrganizationMemberMutation
} from "../../GraphQL/generated/graphql";
import {
    buildOrganizationMemberStatusToggleInput,
    buildOrganizationMemberCreateInput,
    buildOrganizationMemberUpdateInput,
    getOrganizationMemberDisplayName,
    isOrganizationMemberAccount,
    isOrganizationMemberOnboardingQueued,
    isPendingOrganizationMember
} from "./organizationMembersPageHelpers";
import type {OrganizationMemberCreateFormValues, OrganizationMemberUpdateFormValues, OrganizationMemberUser} from "./organizationMembersPageHelpers";
import {ADMIN_MAX_PAGE_SIZE} from "./adminPaging";
import {buildUserFacingErrorLabels, getUserFacingErrorMessage} from "../../userFacingErrorUtils";
import {sortText, textSorter} from "./tableSorters";
import {DeletePopconfirm} from "../../Components/DeleteConfirmation";
import {StatusTag} from "../../Components/ui/StatusTag";

export const OrganizationMembersSection: FC = () => {
    const {message, modal} = App.useApp();
    const {t} = useTranslation();
    const [search, setSearch] = useState("");
    const [createOpen, setCreateOpen] = useState(false);
    const [editingMember, setEditingMember] = useState<OrganizationMemberUser>();
    const [resendingUserId, setResendingUserId] = useState<string>();
    const [updatingUserId, setUpdatingUserId] = useState<string>();
    const [removingUserId, setRemovingUserId] = useState<string>();
    const errorLabels = buildUserFacingErrorLabels(t);

    const membersQuery = useOrganizationMembersQuery({
        variables: {
            take: ADMIN_MAX_PAGE_SIZE
        },
        fetchPolicy: "cache-and-network"
    });
    const [createOrganizationMember, {loading: creating}] = useCreateOrganizationMemberMutation();
    const [resendOnboarding] = useResendOrganizationMemberOnboardingMutation();
    const [updateOrganizationMember, {loading: updating}] = useUpdateOrganizationMemberMutation();
    const [removePendingInvite] = useRemovePendingOrganizationMemberInviteMutation();

    const members = useMemo((): OrganizationMemberUser[] => {
        const term = search.trim().toLocaleLowerCase();
        return (membersQuery.data?.users?.items ?? [])
            .filter(isOrganizationMemberAccount)
            .filter(user => {
                if (!term) {
                    return true;
                }

                return [
                    getOrganizationMemberDisplayName(user),
                    user.email,
                    user.userName,
                    user.phoneNumber
                ]
                    .filter(Boolean)
                    .some(value => value!.toLocaleLowerCase().includes(term));
            });
    }, [membersQuery.data?.users?.items, search]);

    const roleLabel = (role: UserRole) => role === UserRole.Administrator
        ? t("Admin", {defaultValue: "Admin"})
        : t("organization-member", {defaultValue: "Organization member"});

    const statusTag = (user: OrganizationMemberUser) => {
        if (!user.isActive) {
            return <StatusTag tone="neutral">{t("inactive", {defaultValue: "Inactive"})}</StatusTag>;
        }

        if (isPendingOrganizationMember(user)) {
            if (user.onboardingEmailStatus === EmailDeliveryStatus.Queued) {
                return <StatusTag tone="info">{t("email-delivery-queued", {defaultValue: "Queued"})}</StatusTag>;
            }

            const failureReason = user.onboardingEmailFailureReason?.trim();
            if (user.onboardingEmailStatus === EmailDeliveryStatus.Failed || failureReason) {
                return (
                    <Space direction="vertical" size={0}>
                        <StatusTag tone="error">{t("onboarding-email-failed", {defaultValue: "Failed"})}</StatusTag>
                        <Typography.Text type="secondary">{failureReason}</Typography.Text>
                    </Space>
                );
            }

            if ((user.onboardingEmailSentCount ?? 0) > 1) {
                return <StatusTag tone="warning">{t("onboarding-email-resent", {defaultValue: "Resent"})}</StatusTag>;
            }

            if (user.onboardingEmailLastSentAtUtc) {
                return <StatusTag tone="warning">{t("onboarding-email-sent", {defaultValue: "Sent"})}</StatusTag>;
            }

            return <StatusTag tone="warning">{t("pending-onboarding", {defaultValue: "Pending"})}</StatusTag>;
        }

        return <StatusTag tone="success">{t("active", {defaultValue: "Active"})}</StatusTag>;
    };

    const handleCreate = async (values: OrganizationMemberCreateFormValues) => {
        try {
            const result = await createOrganizationMember({
                variables: {
                    input: buildOrganizationMemberCreateInput(values)
                }
            });
            const createdMember = result.data?.createOrganizationMember;
            if (createdMember?.onboardingEmailStatus === EmailDeliveryStatus.Failed) {
                message.warning(t("organization-member-created-email-failed", {
                    defaultValue: "Organization member created, but onboarding email failed. Check SMTP settings and resend onboarding."
                }));
            } else {
                message.success(t("organization-member-created-email-queued", {
                    defaultValue: "Organization member created; onboarding email queued."
                }));
            }
            setCreateOpen(false);
            await membersQuery.refetch();
        } catch (error: any) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        }
    };

    const handleResend = async (user: OrganizationMemberUser) => {
        setResendingUserId(user.id);
        try {
            const result = await resendOnboarding({
                variables: {
                    input: {
                        userId: user.id
                    }
                }
            });
            const resentMember = result.data?.resendOrganizationMemberOnboarding;
            if (resentMember?.onboardingEmailStatus === EmailDeliveryStatus.Failed) {
                message.warning(t("organization-member-onboarding-email-failed", {
                    defaultValue: "Onboarding email failed. Check SMTP settings and resend onboarding."
                }));
            } else {
                message.success(t("organization-member-onboarding-queued", {defaultValue: "Onboarding email queued."}));
            }
            await membersQuery.refetch();
        } catch (error: any) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        } finally {
            setResendingUserId(undefined);
        }
    };

    const handleUpdate = async (user: OrganizationMemberUser, values: OrganizationMemberUpdateFormValues) => {
        setUpdatingUserId(user.id);
        try {
            await updateOrganizationMember({
                variables: {
                    input: buildOrganizationMemberUpdateInput(user, values)
                }
            });
            message.success(t("organization-member-updated", {defaultValue: "Organization member updated"}));
            setEditingMember(undefined);
            await membersQuery.refetch();
        } catch (error: any) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        } finally {
            setUpdatingUserId(undefined);
        }
    };

    const handleToggleActive = async (user: OrganizationMemberUser, isActive: boolean) => {
        const confirmationText = isActive
            ? t("organization-member-reactivate-confirm", {defaultValue: "Reactivate this organization member?"})
            : t("organization-member-deactivate-confirm", {defaultValue: "Deactivate this organization member?"});

        const confirmed = await modal.confirm({
            title: confirmationText,
            okText: t("confirm", {defaultValue: "Confirm"}),
            cancelText: t("cancel", {defaultValue: "Cancel"})
        });

        if (!confirmed) {
            return;
        }

        setUpdatingUserId(user.id);
        try {
            await updateOrganizationMember({
                variables: {
                    input: buildOrganizationMemberStatusToggleInput(user, isActive)
                }
            });
            message.success(isActive
                ? t("organization-member-reactivated", {defaultValue: "Organization member reactivated"})
                : t("organization-member-deactivated", {defaultValue: "Organization member deactivated"}));
            await membersQuery.refetch();
        } catch (error: any) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        } finally {
            setUpdatingUserId(undefined);
        }
    };

    const handleRemovePendingInvite = async (user: OrganizationMemberUser) => {
        setRemovingUserId(user.id);
        try {
            await removePendingInvite({
                variables: {
                    input: {
                        userId: user.id
                    }
                }
            });
            message.success(t("organization-member-invite-removed", {defaultValue: "Pending invite removed"}));
            await membersQuery.refetch();
        } catch (error: any) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        } finally {
            setRemovingUserId(undefined);
        }
    };

    const columns: TableColumnsType<OrganizationMemberUser> = [
        {
            key: "name",
            title: t("name", {defaultValue: "Name"}),
            sorter: textSorter(user => sortText(
                getOrganizationMemberDisplayName(user),
                user.email,
                user.userName
            )),
            render: (_, user) => (
                <Space direction="vertical" size={0}>
                    <Typography.Text strong>{getOrganizationMemberDisplayName(user)}</Typography.Text>
                    <Typography.Text type="secondary">{user.email ?? user.userName ?? "-"}</Typography.Text>
                </Space>
            )
        },
        {
            key: "phone",
            title: t("phone", {defaultValue: "Phone"}),
            sorter: textSorter(user => user.phoneNumber),
            render: (_, user) => user.phoneNumber || "-"
        },
        {
            key: "role",
            title: t("role", {defaultValue: "Role"}),
            sorter: textSorter(user => user.defaultRole),
            render: (_, user) => <StatusTag tone={user.defaultRole === UserRole.Administrator ? "info" : "info"}>{roleLabel(user.defaultRole)}</StatusTag>
        },
        {
            key: "status",
            title: t("status", {defaultValue: "Status"}),
            sorter: textSorter(user => isPendingOrganizationMember(user)
                ? "pending"
                : user.isActive ? "active" : "inactive"),
            render: (_, user) => statusTag(user)
        },
        {
            key: "actions",
            title: t("actions", {defaultValue: "Actions"}),
            align: "right",
            render: (_, user) => (
                <Space wrap size={8} style={{justifyContent: "flex-end"}}>
                    <Button
                        icon={<EditOutlined/>}
                        size="small"
                        onClick={() => setEditingMember(user)}
                    >
                        {t("edit", {defaultValue: "Edit"})}
                    </Button>
                    {isPendingOrganizationMember(user) ? (
                        <>
                            <Button
                                icon={<MailOutlined/>}
                                size="small"
                                loading={resendingUserId === user.id}
                                disabled={isOrganizationMemberOnboardingQueued(user)}
                                onClick={() => handleResend(user)}
                            >
                                {t("resend-onboarding", {defaultValue: "Resend onboarding"})}
                            </Button>
                            <DeletePopconfirm
                                itemType={t("delete-target-invitation", {defaultValue: "pending invitation"})}
                                itemName={user.email ?? user.userName ?? user.id}
                                onConfirm={() => handleRemovePendingInvite(user)}
                            >
                                <Button
                                    danger
                                    icon={<DeleteOutlined/>}
                                    size="small"
                                    loading={removingUserId === user.id}
                                >
                                    {t("organization-member-remove-invite", {defaultValue: "Remove invite"})}
                                </Button>
                            </DeletePopconfirm>
                        </>
                    ) : user.isActive ? (
                        <Button
                            icon={<StopOutlined/>}
                            size="small"
                            loading={updatingUserId === user.id}
                            onClick={() => handleToggleActive(user, false)}
                        >
                            {t("deactivate", {defaultValue: "Deactivate"})}
                        </Button>
                    ) : (
                        <Button
                            icon={<UndoOutlined/>}
                            size="small"
                            loading={updatingUserId === user.id}
                            onClick={() => handleToggleActive(user, true)}
                        >
                            {t("reactivate", {defaultValue: "Reactivate"})}
                        </Button>
                    )}
                </Space>
            )
        }
    ];

    return (
        <section aria-labelledby="organization-members-heading">
            <div className="ati-stack">
                <Typography.Title id="organization-members-heading" level={3} style={{margin: 0}}>
                    {t("organization-members", {defaultValue: "Organization members"})}
                </Typography.Title>

                <Space wrap style={{width: "100%", justifyContent: "space-between"}}>
                    <Input
                        allowClear
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                        placeholder={t("search", {defaultValue: "Search"})}
                        style={{maxWidth: 360}}
                    />
                    <Space wrap>
                        <Button
                            type="primary"
                            icon={<PlusOutlined/>}
                            onClick={() => setCreateOpen(true)}
                        >
                            {t("add-organization-member", {defaultValue: "Add member"})}
                        </Button>
                    </Space>
                </Space>

                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={members}
                    loading={membersQuery.loading}
                    pagination={{pageSize: 20, showSizeChanger: false}}
                />
            </div>

            {createOpen && (
                <OrganizationMemberCreateModal
                    open={createOpen}
                    creating={creating}
                    onCancel={() => setCreateOpen(false)}
                    onCreate={handleCreate}
                />
            )}
            {editingMember && (
                <OrganizationMemberEditModal
                    member={editingMember}
                    open={!!editingMember}
                    saving={updating || updatingUserId === editingMember.id}
                    roleLabel={roleLabel}
                    onCancel={() => setEditingMember(undefined)}
                    onSave={handleUpdate}
                />
            )}
        </section>
    );
};

type OrganizationMemberCreateModalProps = {
    open: boolean;
    creating: boolean;
    onCancel: () => void;
    onCreate: (values: OrganizationMemberCreateFormValues) => Promise<void>;
};

const OrganizationMemberCreateModal: FC<OrganizationMemberCreateModalProps> = ({open, creating, onCancel, onCreate}) => {
    const {t} = useTranslation();
    const [form] = Form.useForm<OrganizationMemberCreateFormValues>();

    const handleOk = async () => {
        const values = await form.validateFields();
        await onCreate(values);
    };

    return (
        <Modal
            title={t("add-organization-member", {defaultValue: "Add member"})}
            open={open}
            onCancel={onCancel}
            onOk={handleOk}
            confirmLoading={creating}
            okText={t("create", {defaultValue: "Add"})}
            cancelText={t("cancel", {defaultValue: "Cancel"})}
            destroyOnHidden
        >
            <Form form={form} layout="vertical" preserve={false}>
                <Form.Item
                    name="email"
                    label={t("email", {defaultValue: "Email"})}
                    rules={[
                        {required: true, message: t("The Email field is required.", {defaultValue: "The Email field is required."})},
                        {type: "email", message: t("InvalidEmail", {defaultValue: "Enter a valid email"})}
                    ]}
                >
                    <Input autoComplete="email"/>
                </Form.Item>
                <Form.Item name="firstName" label={t("firstName", {defaultValue: "First Name"})}>
                    <Input autoComplete="given-name"/>
                </Form.Item>
                <Form.Item name="lastName" label={t("lastName", {defaultValue: "Last Name"})}>
                    <Input autoComplete="family-name"/>
                </Form.Item>
                <Form.Item name="phoneNumber" label={t("phone", {defaultValue: "Phone"})}>
                    <Input autoComplete="tel"/>
                </Form.Item>
            </Form>
        </Modal>
    );
};

type OrganizationMemberEditModalProps = {
    member: OrganizationMemberUser;
    open: boolean;
    saving: boolean;
    roleLabel: (role: UserRole) => string;
    onCancel: () => void;
    onSave: (member: OrganizationMemberUser, values: OrganizationMemberUpdateFormValues) => Promise<void>;
};

const OrganizationMemberEditModal: FC<OrganizationMemberEditModalProps> = ({
    member,
    open,
    saving,
    roleLabel,
    onCancel,
    onSave
}) => {
    const {t} = useTranslation();
    const [form] = Form.useForm<OrganizationMemberUpdateFormValues>();

    const handleOk = async () => {
        const values = await form.validateFields();
        await onSave(member, values);
    };

    return (
        <Modal
            title={t("edit-organization-member", {defaultValue: "Edit member"})}
            open={open}
            onCancel={onCancel}
            onOk={handleOk}
            confirmLoading={saving}
            okText={t("save", {defaultValue: "Save"})}
            cancelText={t("cancel", {defaultValue: "Cancel"})}
            destroyOnHidden
        >
            <Form
                form={form}
                layout="vertical"
                preserve={false}
                initialValues={{
                    firstName: member.firstName ?? "",
                    lastName: member.lastName ?? "",
                    phoneNumber: member.phoneNumber ?? "",
                    defaultRole: member.defaultRole,
                    isActive: member.isActive
                }}
            >
                <Typography.Text type="secondary">{member.email ?? member.userName ?? "-"}</Typography.Text>
                <Form.Item name="firstName" label={t("firstName", {defaultValue: "First Name"})}>
                    <Input autoComplete="given-name"/>
                </Form.Item>
                <Form.Item name="lastName" label={t("lastName", {defaultValue: "Last Name"})}>
                    <Input autoComplete="family-name"/>
                </Form.Item>
                <Form.Item name="phoneNumber" label={t("phone", {defaultValue: "Phone"})}>
                    <Input autoComplete="tel"/>
                </Form.Item>
                <Form.Item name="defaultRole" label={t("role", {defaultValue: "Role"})}>
                    <Select
                        options={[
                            {value: UserRole.User, label: roleLabel(UserRole.User)},
                            {value: UserRole.Administrator, label: roleLabel(UserRole.Administrator)}
                        ]}
                    />
                </Form.Item>
                <Form.Item
                    name="isActive"
                    label={t("active", {defaultValue: "Active"})}
                    valuePropName="checked"
                >
                    <Switch/>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default OrganizationMembersSection;
